import React, { useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ClockIcon, BookMarkedIcon, UsersIcon, SunriseIcon } from 'lucide-react';
export function WhyNowSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="w-full bg-[#111111] py-14 md:py-28 px-6" data-id="element-273">
      <div className="max-w-5xl mx-auto" data-id="element-274">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-275">
          <p className="text-[#CF9B2E] uppercase tracking-[0.3em] text-sm font-bold mb-4 font-['Lato']" data-id="element-276">
            El momento es ahora
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#F5F0E8] mb-6 leading-tight" data-id="element-277">
            Cuatro razones por las que
            <br data-id="element-278" />
            <span className="text-[#CF9B2E]" data-id="element-279">no puedes esperar</span>
          </h2>
          <div className="gold-divider" data-id="element-280" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10" data-id="element-281">
          {/* Reason 1 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="flex gap-6" data-id="element-282">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C0392B]/20 flex items-center justify-center" data-id="element-283">
              <ClockIcon className="w-6 h-6 text-[#C0392B]" data-id="element-284" />
            </div>
            <div data-id="element-285">
              <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-286">
                La memoria no puede esperar
              </h3>
              <p className="text-[#9A9080] leading-relaxed" data-id="element-287">
                Los regímenes cuentan con el olvido. Cada año que pasa sin
                documentar lo ocurrido es una victoria para quienes destruyeron
                Venezuela. Este libro es un acto de resistencia.
              </p>
            </div>
          </motion.div>

          {/* Reason 2 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="flex gap-6" data-id="element-288">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#CF9B2E]/20 flex items-center justify-center" data-id="element-289">
              <BookMarkedIcon className="w-6 h-6 text-[#CF9B2E]" data-id="element-290" />
            </div>
            <div data-id="element-291">
              <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-292">
                Dejar registro para la historia
              </h3>
              <p className="text-[#9A9080] leading-relaxed" data-id="element-293">
                Las generaciones futuras necesitan saber qué pasó realmente. No
                la versión oficial, no la narrativa conveniente — sino la verdad
                cruda y documentada de lo que vivió el pueblo venezolano.
              </p>
            </div>
          </motion.div>

          {/* Reason 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="flex gap-6" data-id="element-294">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#1A5276]/30 flex items-center justify-center" data-id="element-295">
              <UsersIcon className="w-6 h-6 text-[#4A90C4]" data-id="element-296" />
            </div>
            <div data-id="element-297">
              <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-298">
                Un mensaje a las nuevas generaciones
              </h3>
              <p className="text-[#9A9080] leading-relaxed" data-id="element-299">
                Los hijos de la diáspora crecen sin entender completamente por
                qué sus padres lloraban al hablar de su país. Este libro les da
                respuestas. Les devuelve su historia.
              </p>
            </div>
          </motion.div>

          {/* Reason 4 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="flex gap-6" data-id="element-300">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#CF9B2E]/20 flex items-center justify-center" data-id="element-301">
              <SunriseIcon className="w-6 h-6 text-[#CF9B2E]" data-id="element-302" />
            </div>
            <div data-id="element-303">
              <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-304">
                El final de un ciclo político
              </h3>
              <p className="text-[#9A9080] leading-relaxed" data-id="element-305">
                Estamos ante un punto de inflexión histórico. Entender cómo
                llegamos aquí es el primer paso para construir lo que viene.
                Venezuela tiene futuro — pero ese futuro necesita conocer su
                pasado.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}